import { useXClone } from '@/state/xclone';
import { useCinematic } from '@/components/CinematicTakeover';
import { seedFor } from './clock';
import { prngFromSeed } from './prng';
import { addImpulse } from './prices';
import { getTicker } from '@/content/tickers';
import type { TribeId } from '@/content/xcloneHandles';

/**
 * Procedural Mad-Libs headline generator. Templates have {slot} placeholders
 * filled from per-slot dictionaries. Deterministic from clock — same time
 * bucket → same headline across all clients. Adds infinite-feeling variety
 * to the ambient X-Clone feed beyond the curated post pool.
 */

const SLOTS: Record<string, string[]> = {
  pol: [
    'Trump', 'Putin', 'JPow', 'Elon', 'Musk', 'Zuckerberg', 'Sam Altman', 'JD Vance',
    'AOC', 'MTG', 'RFK Jr', 'the Pope', 'Joe Rogan', 'Kanye', 'Diddy', 'Greta Thunberg',
    'an unnamed senator', 'a Saudi prince', 'an offshore lawyer', 'a 14-year-old in Ohio',
    'every cabinet member except one', 'a guy named Steve', 'a former White House aide',
    'a B-list pundit', 'a major-party nominee', 'a podcaster you have never heard of',
    'Tucker Carlson', 'Ben Shapiro', 'Hasan Piker', 'Andrew Tate', 'Bobby Kennedy III',
    'Ron DeSantis', 'Gavin Newsom', 'Bernie Sanders', 'Pete Buttigieg', 'Marjorie Taylor Greene',
    'a billionaire who unfollowed you', 'a journalist who blocked you', 'a former friend',
    'a Trump-cabinet spokesperson', 'a CNN anchor between segments', 'a reformed crypto bro',
    'a cabinet member who has never been confirmed', 'a senator who time forgot',
    'a podcaster\'s podcaster', 'a guy who lost everything in 2017 and keeps tweeting about it',
  ],
  ticker: [
    '$TARI', '$BOOM', '$GRFT', '$SHIL', '$DUMP', '$FAFO', '$OAEN', '$MUSK', '$REKT',
    '$PHRM', '$HEAL', '$WIPE', '$VAPE', '$DRAFT', '$MEAL', '$ROAR', '$CHIP', '$BURG',
    '$PRAY', '$CRTL', '$FAKE', '$DOGE2', '$RUGS', '$JEET', '$EXIT',
    '$POPE', '$WALL', '$POLL', '$SCAM', '$SUBS',
  ],
  pct: ['0.4%', '1.2%', '2%', '3.4%', '4%', '5%', '7.8%', '11%', '14%', '18%', '23%', 'a record amount'],
  reaction: [
    'analysts split', 'analysts unmoved', 'analysts confused', 'analysts vibrating',
    'markets reacting', 'markets pretending not to', 'CNBC bracing', 'r/wsb celebrating',
    'finance Twitter dunking', 'press dunking from a moral position they have not earned',
    'Wall Street briefly pausing to look', 'Wall Street going back to lunch',
    'volatility ETFs printing', 'gold quietly catching a bid',
    'crypto bros calling it bullish for unrelated reasons',
    'Powell unavailable for comment',
    '14 reporters all asking the same question slightly differently',
    'r/personalfinance subscribers unfollowing in protest',
    'one hedge fund actually positioned for this; the rest absolutely were not',
    'a bond trader visibly shaking',
  ],
  weirdNumber: [
    '41', '69', '420', '1,400', '12,000', '$87 million', '$2 billion', '$47',
    '$0.04', '$1.4 trillion', '7.2', '0', '14', 'a record amount',
    'enough to buy Vermont', '$420.69', 'three', '$NaN',
  ],
  presAct: [
    'bombs', 'sanctions', 'invades', 'nationalizes', 'seizes', 'pardons', 'fires the entire',
    'replaces with a chatbot', 'declares war on', 'auctions on eBay', 'renames after himself',
    'tariffs at 500%', 'nukes', 'sells to a Saudi prince', 'strikes via drone', 'deports',
    'replaces the SEC for', 'orders airstrikes on', 'evicts', 'truths about',
  ],
  presTarget: [
    'Germany', 'Belgium', 'Canada', 'Mexico', 'Switzerland', 'Greenland', 'Mar-a-Lago\'s neighbor',
    'a Tim Hortons', 'a Cracker Barrel', 'a McDonald\'s drive-thru', 'a single Costco', 'the moon',
    'Hurricane Brenda', 'NATO HQ', 'the Federal Reserve', 'his own polling firm', 'the SEC',
    'an unnamed continent', 'a country he just invented', 'the Pacific Ocean', 'Antarctica',
    'an Iowa cornfield', 'a 7-Eleven', 'the U.N. building', 'a chatbot named Clyde',
  ],
  presReact: [
    'rips', 'rallies', 'craters', 'plunges', 'moons', 'tanks', 'soars', 'collapses',
    'spikes', 'sinks', 'detonates upward', 'goes vertical', 'goes vertical',
  ],
  country: [
    'Germany', 'France', 'Belgium', 'Canada', 'Mexico', 'Switzerland', 'Sweden', 'Iceland',
    'a country he just heard about', 'a country he can\'t pronounce', 'Andorra', 'Liechtenstein',
    'Cuba', 'North Korea', 'an island he claims to own', 'Mars', 'the moon', 'Atlantis',
  ],
  execOrderText: [
    'banning Tuesdays', 'making it illegal to short \$TRUMP', 'renaming the dollar to "the Trump"',
    'pardoning himself for everything', 'declaring himself a third term', 'abolishing the IRS',
    'nationalizing the Mall of America', 'auctioning the Department of Education',
    'replacing all Federal judges with one loyal person', 'declaring Mars US territory',
    'making Mar-a-Lago the new capital', 'firing the entire Pentagon by group text',
    'paying the national debt "with vibes"', 'invading a state to test it',
  ],
  powellAct: [
    'cuts rates to -14%', 'raises rates 500bp "out of spite"', 'resigns mid-press conference',
    'is "accidentally" deported by ICE', 'orders the Fed to "vibe"', 'admits inflation is fake',
    'breaks down crying on live TV', 'announces "rates are zero forever"',
    'is replaced by a 22-year-old podcaster', 'declares a new currency called "the Powell"',
  ],
  weirdNoun: [
    'tariffs', 'his own indictment', 'a thing that did not happen', 'the Fed',
    'a typo', 'an unnamed celebrity', 'his ex-wife', 'a trade deal that does not exist',
    'a country he just heard about', 'a podcast he was on once', 'TikTok',
    'something a 14-year-old said about him', 'his net worth', 'gas prices',
    'a drone strike that may not have occurred', 'Bitcoin', 'AI', 'rats in cities',
    'a Bloomberg article he did not read', 'a Wikipedia paragraph someone screenshotted',
    'his own deposition', 'a Nielsen rating', 'the price of a Big Mac',
    'a typo in a 2014 tweet', 'his daughter\'s wedding',
    'a country he forgot existed', 'his own LLC', 'a podcast he allegedly was on',
    'an AI deepfake that may be real', 'a property he bought sight-unseen',
    'a hostage exchange he was not part of', 'a sentence he later denied saying',
    'a charity he started yesterday', 'an unflattering photo from 2009',
    'a settlement amount he did not authorize', 'a press conference he forgot to call',
    'a memecoin nobody asked for', 'a foreign election he was definitely not interfering in',
    'a federal judge who asked him to stop', 'a fishing trip on someone else\'s yacht',
    'a NDA he says he never signed', 'a book he didn\'t write', 'a tweet that someone else screenshotted',
    'a deportation order misaddressed to a 4th-grade teacher', 'a TruthSocial post that was 47 lines long',
    'a Senate vote he wasn\'t aware of', 'a Caribbean island he claims to own',
    'a drone he assumed was his own', 'a meme generated by an AI he does not understand',
    'a building permit he definitely did not bribe for', 'a divorce he keeps forgetting about',
    'a charity gala he was uninvited from', 'an offshore account he says is "domestic, actually"',
    'a Joe Rogan clip that has 47M views', 'a campaign donation that "predates" the campaign',
    'a deposition transcript he hasn\'t read', 'a 14-year-old\'s unauthorized fan-cam',
    'a Vatican statement he disagrees with', 'a tariff he announced and unannounced in the same hour',
  ],
  weirdAct: [
    'announces', 'denies', 'apologizes for', 'tweets through', 'live-streams',
    'monetizes', 'rebrands', 'opens an OnlyFans about', 'subtweets', 'sues',
    'launches a podcast about', 'goes on Joe Rogan to discuss', 'Truth-posts about',
    'calls a press conference to address', 'denies any knowledge of',
    'releases a 14-tweet thread about', 'gets cancelled for', 'gets uncancelled for',
    'NFTs', 'crypto-pumps', 'starts a Substack about', 'launches a SPAC about',
    'live-tweets through', 'dunks on', 'leaks', 'subpoenas', 'blocks Reuters about',
    'starts a memecoin called', 'announces and immediately cancels', 'rage-posts about',
    'live-streams a meltdown about', 'gets a tattoo of', 'sends a cease-and-desist about',
    'doxxes himself by accident over', 'pivots to', 'invests heavily in',
    'launches a 14-week speaking tour about', 'gets banned from LinkedIn for',
    'records a 2-hour deposition about', 'commissions an oil portrait of',
    'auctions a private dinner about', 'vlogs from a panic room about',
    'Cameo-pivots to', 'sends a single emoji about',
  ],
  // Gerund forms — used by `{X} caught {weirdActIng} ...` templates so we don't
  // produce "monetizesing" by naively appending "ing" to present-tense verbs.
  weirdActIng: [
    'announcing', 'denying', 'apologizing for', 'tweeting through', 'live-streaming',
    'monetizing', 'rebranding', 'opening an OnlyFans about', 'subtweeting', 'suing',
    'launching a podcast about', 'going on Joe Rogan to discuss', 'Truth-posting about',
    'calling a press conference to address', 'denying any knowledge of',
    'releasing a 14-tweet thread about', 'getting cancelled for', 'getting uncancelled for',
    'minting NFTs of', 'crypto-pumping', 'starting a Substack about', 'launching a SPAC about',
    'live-tweeting through', 'dunking on', 'leaking', 'subpoenaing', 'blocking Reuters about',
    'starting a memecoin called', 'announcing and immediately cancelling', 'rage-posting about',
    'live-streaming a meltdown about', 'getting a tattoo of', 'sending a cease-and-desist about',
    'accidentally doxxing himself over', 'pivoting to', 'investing heavily in',
    'booking a 14-week speaking tour about', 'getting banned from LinkedIn for',
    'recording a 2-hour deposition about', 'commissioning an oil portrait of',
    'auctioning a private dinner about', 'vlogging from a panic room about',
    'Cameo-pivoting to', 'sending a single emoji about',
  ],
};

const TEMPLATES: { weight: number; template: string; tribe: TribeId; tickerSlot?: 'ticker' }[] = [
  // S-tier presidential templates — these classify as 'S' (massive impulses) due to "PRESIDENT" / military lingo
  { weight: 1.5, tribe: 'press', template: 'BREAKING: PRESIDENT {presAct} {presTarget}. {ticker} {presReact} {pct}.' },
  { weight: 1.0, tribe: 'press', template: 'BREAKING: PRESIDENT bombs {country} via Truth post. {ticker} {presReact} {pct}.' },
  { weight: 1.0, tribe: 'press', template: 'BREAKING: PENTAGON strike on {presTarget}. justification: "{weirdNoun}." {ticker} {presReact} {pct}.' },
  { weight: 0.8, tribe: 'press', template: 'BREAKING: PRESIDENT signs executive order {execOrderText}. {ticker} {presReact} {pct}.' },
  { weight: 0.8, tribe: 'press', template: 'BREAKING: NUCLEAR strike on {presTarget}. {reaction}.' },
  { weight: 0.6, tribe: 'press', template: 'FED EMERGENCY: Powell {powellAct}. {ticker} {presReact} {pct}.' },
  // Additional S-tier presidential templates
  { weight: 0.8, tribe: 'press', template: 'BREAKING: PRESIDENT declares {presTarget} a "national security threat." {ticker} {presReact} {pct}.' },
  { weight: 0.7, tribe: 'press', template: 'BREAKING: PRESIDENT pardons himself for {weirdNoun}. {ticker} {presReact} {pct}.' },
  { weight: 0.7, tribe: 'press', template: 'BREAKING: PRESIDENT renames {presTarget} after himself. {ticker} {presReact} {pct}.' },
  { weight: 0.7, tribe: 'press', template: 'PENTAGON: kinetic strike package authorized against {country}. {ticker} {presReact} {pct}. {reaction}.' },
  { weight: 0.6, tribe: 'press', template: 'BREAKING: PRESIDENT seizes {presTarget} via emergency executive order. constitutional scholars: split. {ticker} {presReact} {pct}.' },
  // A-tier major figures
  { weight: 1.0, tribe: 'press', template: 'BREAKING: {pol} {weirdAct} {weirdNoun}. {ticker} reacts {pct}.' },
  { weight: 1.0, tribe: 'press', template: '{pol} {weirdAct} {weirdNoun} on live TV. {reaction}.' },
  { weight: 1.0, tribe: 'press', template: 'BREAKING: {pol} caught {weirdActIng} {weirdNoun}. {ticker} unaffected. {reaction}.' },
  { weight: 0.9, tribe: 'press', template: '*sources confirm* {pol} privately discussed {weirdNoun}. {reaction}.' },
  { weight: 1.0, tribe: 'press', template: '{ticker} up {pct} on {pol} {weirdActIng} {weirdNoun}. unclear how the math works.' },
  { weight: 0.9, tribe: 'fintwit', template: 'unusual options activity in {ticker}. by which i mean: {pol} just tweeted about {weirdNoun}.' },
  { weight: 0.9, tribe: 'wsb', template: 'just yolo\'d {weirdNumber} into {ticker} because {pol} mentioned {weirdNoun}. wish me luck.' },
  { weight: 0.7, tribe: 'maga', template: '{ticker} pumping because {pol} understands {weirdNoun}. CNN won\'t cover this.' },
  { weight: 0.7, tribe: 'left', template: '{pol} is, again, {weirdActIng} {weirdNoun}. media will frame this as a both-sides issue. {ticker} unaffected because nothing matters.' },
  { weight: 0.8, tribe: 'crypto', template: '{ticker} is the new {ticker}. WAGMI.' },
  { weight: 0.6, tribe: 'cnbc', template: 'EXCLUSIVE: {pol} expected to {weirdAct} {weirdNoun} this hour. analysts split.' },
  { weight: 0.7, tribe: 'press', template: '{pol} apologizes for {weirdActIng} {weirdNoun}. apology immediately rescinded. {reaction}.' },
  { weight: 0.8, tribe: 'press', template: 'BREAKING: {pol} and {pol} reportedly in private discussions about {weirdNoun}. {reaction}.' },
  { weight: 0.7, tribe: 'wsb', template: 'tell me why {pol} just {weirdAct} {weirdNoun} and i\'m up {pct} on {ticker} calls. genuinely no idea why.' },
  { weight: 0.7, tribe: 'fintwit', template: '{pol} {weirdActIng} {weirdNoun} is, for {ticker} holders, the only thing happening this week. tape unmoved.' },
  { weight: 0.6, tribe: 'maga', template: 'media silent as {pol} {weirdAct} {weirdNoun}. real Americans paying attention. {ticker}.' },
  { weight: 0.6, tribe: 'left', template: 'reminder that {pol} is currently {weirdActIng} {weirdNoun} and we are all just supposed to be normal about this. {ticker} up {pct}.' },
  { weight: 0.6, tribe: 'crypto', template: '{ticker} pumping because {pol} mentioned {weirdNoun}. fundamentals strong.' },
  { weight: 0.5, tribe: 'press', template: 'POLL: should {pol} {weirdAct} {weirdNoun}? results: {pct} yes, {pct} no, {pct} undecided. margin of error: {pct}.' },
  { weight: 0.5, tribe: 'press', template: 'NEW: {pol} statement on {weirdNoun} consists of three words and one image of an eagle.' },
  // Late-batch additions — same slot system, more variety per session
  { weight: 0.7, tribe: 'wsb', template: 'BREAKING: my account hit {weirdNumber}. it will not stay there. it has never stayed there.' },
  { weight: 0.6, tribe: 'wsb', template: '{ticker} {presReact} {pct} and i was 100% in cash for it. exactly as planned. by which i mean: not at all.' },
  { weight: 0.7, tribe: 'fintwit', template: '{ticker} just printed a 4-hour {presReact}. textbook. you can find this exact pattern in zero textbooks.' },
  { weight: 0.6, tribe: 'fintwit', template: 'three things are true about {ticker} this morning: it moved, you noticed, and the noticing is the trade.' },
  { weight: 0.7, tribe: 'cnbc', template: 'we go now to {pol}, who is, as ever, somewhere very loud doing something about {weirdNoun}.' },
  { weight: 0.6, tribe: 'cnbc', template: 'we asked the tape what it thinks. the tape said {pct}. we are still parsing this.' },
  { weight: 0.6, tribe: 'maga', template: 'they want you to sell {ticker}. we want you to buy more {ticker}. who do you trust.' },
  { weight: 0.5, tribe: 'maga', template: '{pol} owns {ticker}. that is the only research note you will ever need.' },
  { weight: 0.6, tribe: 'left', template: 'a reminder that the {ticker} rally is being celebrated by people who, last quarter, said the same exact thing about {ticker}.' },
  { weight: 0.5, tribe: 'left', template: 'late-stage capitalism update: {ticker} is up {pct}, and that is somehow the headline.' },
  { weight: 0.7, tribe: 'crypto', template: '{ticker} just printed a candle. the candle has meaning. we will not explain the meaning. WAGMI.' },
  { weight: 0.6, tribe: 'crypto', template: 'on-chain analysis confirms: {ticker} is doing a thing. the thing is bullish. always bullish.' },
  { weight: 0.5, tribe: 'press', template: 'EXCLUSIVE: 14 anonymous senior officials, who are all the same person, confirm {pol} is considering {weirdNoun}.' },
  { weight: 0.5, tribe: 'press', template: 'analysis: this is the worst {weirdNoun} crisis since the last {weirdNoun} crisis 11 weeks ago. {ticker} unmoved.' },
  { weight: 0.4, tribe: 'mom', template: 'sweetie, did you sell that {ticker} stock yet? Janet says {pol} is going to {weirdAct} {weirdNoun}. she heard it on Facebook.' },
  { weight: 0.4, tribe: 'mom', template: 'i don\'t understand any of this. but i\'m proud of you. unless this is bad. is this bad?' },

  // Wave 8 — more variety
  { weight: 0.6, tribe: 'press', template: 'BREAKING: a thing happened. a different thing also happened. the things are, allegedly, related. {ticker} {presReact} {pct}.' },
  { weight: 0.6, tribe: 'press', template: 'NEW: {pol} now has 14 nicknames for a single rival. analysts at the White House are working through them in alphabetical order.' },
  { weight: 0.6, tribe: 'press', template: '{pol} reportedly told a small group, off the record, that they would, on the record, never have said what they said. {ticker} unmoved.' },
  { weight: 0.5, tribe: 'press', template: 'EXCLUSIVE: {pol} ordered the firing of {presTarget}. the firing has not been carried out. the order has not been rescinded. limbo continues.' },
  { weight: 0.7, tribe: 'fintwit', template: 'reading the tape on {ticker} you can\'t help but notice that nothing has happened in 9 minutes. that itself is a setup.' },
  { weight: 0.7, tribe: 'fintwit', template: 'every time {ticker} prints a fresh ATH the same 14 accounts post the same 14 chart patterns. one of them is, statistically, going to be right. eventually.' },
  { weight: 0.6, tribe: 'fintwit', template: 'i bought {ticker} at {pct}. it is now at {pct}. these are the same number. that is, in this market, a real edge.' },
  { weight: 0.7, tribe: 'cnbc', template: 'we have {pol} on the line in 6 minutes. {pol} will be saying something. that something will move {ticker}. probably. one direction.' },
  { weight: 0.6, tribe: 'cnbc', template: 'a viewer asked: \"is this the top.\" the answer, journalistically, is yes and no. mostly no. but yes.' },
  { weight: 0.7, tribe: 'wsb', template: 'just opened a position in {ticker} large enough that my broker called. she sounded concerned. that\'s the floor.' },
  { weight: 0.7, tribe: 'wsb', template: 'down {pct} on {ticker}. up {pct} in life experience. the math doesn\'t work but the spirit does.' },
  { weight: 0.7, tribe: 'wsb', template: '{ticker} just hit a new low. i added. i added at the previous low. i have, technically, been adding for 6 weeks. eventually it ends.' },
  { weight: 0.6, tribe: 'crypto', template: '{ticker} is currently being adopted by 14 institutions. \"adopted\" means \"a junior associate read about it.\" but. WAGMI.' },
  { weight: 0.6, tribe: 'crypto', template: 'unironically: {ticker} solves a problem nobody has. that is, in crypto, the bullish case.' },
  { weight: 0.5, tribe: 'left', template: 'not to derail but the structural forces moving {ticker} are also the structural forces preventing affordable housing. these are the same forces.' },
  { weight: 0.5, tribe: 'maga', template: 'the people calling {ticker} a bubble are the same people who said gold was a bubble at \$1,200. gold is now \${weirdNumber}. we win again.' },

  // Wave 9 — additional templates for variety
  { weight: 0.5, tribe: 'press', template: 'BREAKING: {pol} reportedly considering a run for {country}\'s presidency. Citizenship status: ambiguous. ${ticker} reacts {pct}.' },
  { weight: 0.6, tribe: 'press', template: 'sources say {pol} accidentally posted their own group-chat screenshot. four reporters are now reading it carefully. {ticker} jumpy.' },
  { weight: 0.6, tribe: 'fintwit', template: 'unusual options activity: someone bought {weirdNumber} weekly calls on {ticker}. likely a bot. likely a billionaire. these are, in this market, the same.' },
  { weight: 0.6, tribe: 'cnbc', template: 'a panel of analysts will now spend 47 minutes discussing whether the move in {ticker} is "real." it is real. they will not conclude that. tune in.' },
  { weight: 0.6, tribe: 'wsb', template: 'just realized i\'ve been holding {ticker} for 6 weeks. nothing has happened. that\'s a rally relative to my other positions.' },
  { weight: 0.6, tribe: 'wsb', template: 'the candle on {ticker} has formed a "Hindenburg Doom Cross." this is a thing i made up. but the chart looks like it.' },
  { weight: 0.5, tribe: 'crypto', template: '{ticker} solves coordination problems. except for the actual coordination of the team building it. WAGMI.' },
  { weight: 0.5, tribe: 'crypto', template: 'just sent {weirdNumber} {ticker} to the burn address. supply now finite. price now infinite. math.' },
  { weight: 0.5, tribe: 'left', template: 'the {ticker} pump is being driven by, structurally, the same five accounts that drove the previous {ticker} pump. circles within circles.' },
  { weight: 0.5, tribe: 'maga', template: 'the founders of {ticker} were America Firsters before America First was a thing. respect. this is your patriotic stock pick.' },
  { weight: 0.5, tribe: 'press', template: 'EXCLUSIVE: a source close to {pol} says they have, in fact, never met {pol}. the source is, allegedly, {pol}\'s former chief of staff.' },
  { weight: 0.5, tribe: 'press', template: 'BREAKING: PRESIDENT cancels his own rally to attend a different rally hosted by himself. ${ticker} reacts to the meta-event.' },
  { weight: 0.4, tribe: 'mom', template: 'sweetie i don\'t know what {ticker} is but janet bought it on facebook and now she won\'t stop talking about it. love mom.' },
];

function pickFromSeed<T>(arr: T[], rng: () => number): T {
  const idx = Math.floor(rng() * arr.length);
  return arr[Math.min(idx, arr.length - 1)]!;
}

function fillTemplate(tpl: string, rng: () => number): string {
  return tpl.replace(/\{(\w+)(\.\w+)?\}/g, (_match, slot: string) => {
    const dict = SLOTS[slot];
    if (!dict) return `{${slot}}`;
    return pickFromSeed(dict, rng);
  });
}

export function generateProceduralHeadline(seedKey: string): { tribe: TribeId; text: string } | null {
  const rng = prngFromSeed(seedFor(seedKey, 0, 1));
  const totalWeight = TEMPLATES.reduce((acc, t) => acc + t.weight, 0);
  const r = rng() * totalWeight;
  let cum = 0;
  for (const t of TEMPLATES) {
    cum += t.weight;
    if (r <= cum) {
      return { tribe: t.tribe, text: fillTemplate(t.template, rng) };
    }
  }
  return null;
}

const handlesByTribe: Record<TribeId, string[]> = {
  wsb: ['YOLOPapi', 'GUHGod69'],
  fintwit: ['MacroDad42', 'TaylorBeartrap'],
  cnbc: ['JimCranier', 'TiaBanzai'],
  maga: ['PatriotPete1776', 'FreedomEagleX'],
  left: ['ProfPolicyWonk', 'SocialistPapaya'],
  crypto: ['WAGMI_VIP', 'ShitcoinPaul', 'DegenWolfie'],
  press: ['ReutersFlash', 'WSJ_RealTimeBait'],
  mom: ['YourMomReal'],
};

export function emitProceduralHeadline(seedKey: string): void {
  const h = generateProceduralHeadline(seedKey);
  if (!h) return;
  const handles = handlesByTribe[h.tribe];
  const handle = handles[Math.floor(Math.random() * handles.length)] ?? 'anon';
  useXClone.getState().receivePost({
    authorHandle: handle,
    authorTribe: h.tribe,
    text: h.text,
    likes: 100 + Math.floor(Math.random() * 6000),
    reposts: 5 + Math.floor(Math.random() * 1000),
  });
  // News moves markets: any $TICKER mentioned in the headline gets an impulse
  // weighted by the SOURCE TIER (Mad-Libs random rando = noise; press = small;
  // presidential / nuclear / strike / war language = massive).
  applyHeadlineImpulse(h.text, h.tribe);
}

/**
 * Weight classes for how much a piece of news moves markets. The whole point
 * is asymmetry — a random degen on Y tweeting "$TARI mooning" should be
 * background noise, while "BREAKING: PRESIDENT BOMBS GERMANY" should crater
 * the tape. The tier roughly maps to the source's actual market authority.
 */
export type ImpulseTier = 'S' | 'A' | 'B' | 'C' | 'D';

// (peakLogDelta range, durationMs range)
const TIER_BANDS: Record<ImpulseTier, { abs: [number, number]; ms: [number, number] }> = {
  S: { abs: [0.12, 0.30], ms: [25 * 60_000, 60 * 60_000] }, // ±12-30%, 25-60min — presidential / nuclear / bomb-a-country tier
  A: { abs: [0.04, 0.10], ms: [10 * 60_000, 25 * 60_000] }, // ±4-10%, 10-25min — major political/macro event
  B: { abs: [0.015, 0.04], ms: [5 * 60_000, 12 * 60_000] }, // ±1.5-4%, 5-12min — press breaking, named secondary figure
  C: { abs: [0.003, 0.012], ms: [3 * 60_000, 7 * 60_000] }, // ±0.3-1.2%, 3-7min — tribe NPC mentioning ticker
  D: { abs: [0.0005, 0.003], ms: [60_000, 4 * 60_000] },    // ±0.05-0.3%, 1-4min — random Mad-Libs noise
};

const TICKER_RE = /\$([A-Z0-9]{2,6})\b/g;

/**
 * Heuristic tier classifier. Pass in (text, tribe?) and it figures out the
 * weight class. PRESIDENTIAL / nuclear / bomb / strike / war markers force
 * the highest tier regardless of tribe. Tribe drives default for routine news.
 */
function classifyTier(text: string, tribe?: TribeId): ImpulseTier {
  const lc = text.toLowerCase();
  // S — actual high-stakes presidential / military / institutional shocks
  if (/\b(president|the white house|nuclear|nukes|bombs|bombed|invasion|invades|strike on|airstrike|martial law|declares war|capitol|sec emergency|fed emergency|powell resigns|coup|impeach|impeached|nationalize|nationalized|seizes|assassinat)/i.test(text)) {
    return 'S';
  }
  // A — major named-figure acts
  if (/\b(trump|powell|musk|fed|fomc|scotus|congress|senate vote|treasury|pentagon|cia|fbi raids|nato|tariff hike|tariff slap|sanction)/i.test(text)) {
    return 'A';
  }
  // Press breaking → B by default
  if (tribe === 'press' || /\bbreaking\b/i.test(lc)) return 'B';
  // wsb/crypto/fintwit/maga/left/cnbc/mom NPC posts → C or D
  if (tribe === 'cnbc' || tribe === 'mom') return 'D';
  if (tribe === 'wsb' || tribe === 'crypto' || tribe === 'fintwit' || tribe === 'maga' || tribe === 'left') return 'C';
  return 'D';
}

const POS_RE = /\b(up|rally|rallies|beats|pump|pumps|pumping|ath|breakout|surge|soars|moon|moons|wagmi|win|winning|bullish|crush|crushed|spike|spikes|ripping|rip|melting up|melts up|nationalize|wins|approved|cleared|exonerat|nuke|nukes|bomb|bombs)\b/i;
const NEG_RE = /\b(down|miss|missed|dump|dumps|crash|crashes|rugpull|rugged|sell|plunge|plunges|sinks|tank|tanks|tanking|bearish|guh|rekt|rip|cratered|fraud|indicted|raid|raided|fired|liquidat|halt|halted|delisted|seizes|seized|martial law|invasion|sanctioned|sanctions)\b/i;

/**
 * Correlation baskets. When an S/A-tier news event hits a "topic" — war,
 * Fed, tech, crypto, geopolitical — the entire basket moves, not just the
 * literally-mentioned ticker. Sign indicates direction relative to the event:
 * positive entries co-move with the headline, negative entries inverse-move.
 */
type Basket = Record<string, number>; // ticker → ±1 sign multiplier
const BASKETS: Record<string, Basket> = {
  // War / military / presidential strike: defense pumps, semis crater (supply chain),
  // tech tanks (risk-off), crypto pumps (flight + chaos), domestic-policy plays rally.
  war: {
    BOOM: 1, WALL: 1, TARI: 1, GRFT: 1, NUKE: 1, GUNZ: 0.8,
    POPE: 0.5, PRAY: 0.5, BTCO: 0.5, TRUMP: 0.5,
    CHIP: -1, OAEN: -1, AGI: -0.5, FAKE: -0.5, MEAL: -0.3, BURG: -0.3,
  },
  // Fed / macro / rate-cut: tech rips, crypto rips, defense fades.
  fed: {
    CHIP: 1, OAEN: 1, AGI: 1, FAKE: 0.5, MUSK: 1, BTCO: 1, DOGE2: 0.5, TRUMP: 0.5,
    BOOM: -0.3, TARI: -0.3,
  },
  // Crypto event: every memecoin rips, $RUGS the most volatile.
  crypto: {
    BTCO: 1, DOGE2: 1, JEET: 1, RUGS: 1, PEPE7: 1, TRUMP: 1, MELON: 0.5, PUMP: 1, AGI: 0.5, FOMO2: 1,
  },
  // Tech / AI: semis + AI tickers move together.
  tech: {
    CHIP: 1, OAEN: 1, AGI: 1, FAKE: 1, MUSK: 0.5, SAAS: 0.7,
  },
  // Geopolitical (Iran/China/Russia): defense + oil narratives + crypto haven.
  geopolitical: {
    BOOM: 1, WALL: 0.5, TARI: 0.5, BTCO: 0.5,
    CHIP: -0.5, OAEN: -0.3, MEAL: -0.3,
  },
  // Tariff / trade-war: tariff plays rip, retailers tank.
  trade: {
    TARI: 1, WALL: 0.5, BOOM: 0.3, GRFT: 0.5,
    BURG: -0.5, MEAL: -0.5, ZZZZ: -0.5, FAKE: -0.3, CHIP: -0.5,
  },
};

function selectBasket(text: string): keyof typeof BASKETS | null {
  const lc = text.toLowerCase();
  if (/\b(bomb|nuke|nuclear|strike|airstrike|invasion|invades|martial law|war|declares war|pentagon|missile)/i.test(text)) return 'war';
  if (/\b(china|russia|iran|nato|taiwan|north korea|sanction)/i.test(lc)) return 'geopolitical';
  if (/\b(fed|fomc|powell|rate cut|rate hike|jpow|treasury|cpi|inflation)/i.test(lc)) return 'fed';
  if (/\b(tariff|trade deal|trade war|tariffs)/i.test(lc)) return 'trade';
  if (/\b(crypto|bitcoin|btc|memecoin|rugpull|$btco|$doge|$jeet|$rugs|$trump|wagmi)/i.test(lc)) return 'crypto';
  if (/\b(ai|chatgpt|openai|chip|semiconductor|altman)/i.test(lc)) return 'tech';
  return null;
}

let lastCinematicAt = 0;
const CINEMATIC_COOLDOWN_MS = 90_000;

export function applyHeadlineImpulse(text: string, tribe?: TribeId): void {
  const seen = new Set<string>();
  const tier = classifyTier(text, tribe);
  const band = TIER_BANDS[tier];

  // S-tier shock: full-screen BREAKING cinematic, throttled so a basket cascade
  // doesn't fire 5 banners back-to-back. Player can dismiss with tap/esc.
  if (tier === 'S') {
    const nowMs = Date.now();
    if (nowMs - lastCinematicAt > CINEMATIC_COOLDOWN_MS) {
      lastCinematicAt = nowMs;
      useCinematic.getState().fire({
        id: `breaking-${nowMs}`,
        type: 'breaking',
        headline: text.length > 240 ? text.slice(0, 237) + '…' : text,
        subhead: 'markets reacting in real time.',
        durationMs: 3500,
      });
    }
  }
  const positive = POS_RE.test(text);
  const negative = NEG_RE.test(text);
  // Direction: + if headline reads positive, − if negative, 0 (jitter) otherwise
  const dir = positive && !negative ? 1 : (negative && !positive ? -1 : 0);
  const baseMag = band.abs[0] + Math.random() * (band.abs[1] - band.abs[0]);

  // 1. Move literally-mentioned tickers
  let m: RegExpExecArray | null;
  TICKER_RE.lastIndex = 0;
  while ((m = TICKER_RE.exec(text)) !== null) {
    const id = m[1]!;
    if (seen.has(id)) continue;
    seen.add(id);
    if (!getTicker(id)) continue;
    let logDelta: number;
    if (dir !== 0) logDelta = dir * baseMag;
    else logDelta = (Math.random() - 0.5) * 2 * band.abs[0];
    const dur = band.ms[0] + Math.floor(Math.random() * (band.ms[1] - band.ms[0]));
    addImpulse({ ticker: id, startMs: Date.now(), durationMs: dur, logDelta });
  }

  // 2. For S/A-tier events, propagate to the correlated basket — even if the
  // headline only literally names one ticker, the whole basket moves.
  // Smaller magnitude on the basket so the named ticker remains the leader.
  if ((tier === 'S' || tier === 'A') && dir !== 0) {
    const basketKey = selectBasket(text);
    const basket = basketKey ? BASKETS[basketKey] : undefined;
    if (basket) {
      const basketMag = baseMag * 0.55; // basket moves ~half as hard as the named name
      for (const [bTicker, sign] of Object.entries(basket)) {
        if (seen.has(bTicker)) continue;       // already moved
        if (!getTicker(bTicker)) continue;
        // Modest jitter so basket members don't move identically
        const jitter = 0.85 + Math.random() * 0.3;
        const logDelta = dir * sign * basketMag * jitter;
        const dur = band.ms[0] + Math.floor(Math.random() * (band.ms[1] - band.ms[0]));
        addImpulse({ ticker: bTicker, startMs: Date.now(), durationMs: dur, logDelta });
        seen.add(bTicker);
      }
    }
  }
}
